/**
 * Settings: Profile, Security (two-step, password), Privacy and safety,
 * Sessions, Appearance, Voice & audio, Notifications, Sounds, Server, About.
 * A full-window layer below the title bar (docs/design/visual-overhaul.md
 * §6.9; Layer.tsx), with the rows and cards of §7.24.
 */
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useT } from '../i18n';
import { goSettings, type SettingsSection } from '../store/ui';
import type { IconName } from '../ui/kit';
import { useTopOnChange } from '../ui/scroll';
import { AboutSection, AppearanceSection, NotificationsSection } from './Appearance';
import { SetLayer, SetNav } from './Layer';
import { PrivacySection } from './Privacy';
import { ProfileSection } from './Profile';
import { SecuritySection, SessionsSection } from './Security';
import { VoiceSection } from './Voice';
import { SoundsSection } from './Sounds';
import { ServerSection } from './Server';
import { PokeAlertSettings } from '../pokes/PokeSettings';
import { ServerNotifyLink } from '../notify/ServerNotifyLink';
import { ActivitySection } from '../social/ActivitySettings';
import { NotifyDeviceSettings } from '../notify/NotifySettings';

const SECTIONS: Array<{
  id: SettingsSection;
  group: 'user' | 'app';
  icon: IconName;
  label: string;
}> = [
  { id: 'profile', group: 'user', icon: 'user', label: 'ns.set.profile' },
  { id: 'security', group: 'user', icon: 'shield', label: 'security.title' },
  { id: 'privacy', group: 'user', icon: 'eye', label: 'um.privacy.title' },
  // rich presence (2026-10-29): Share my activity, Registered games
  { id: 'activity', group: 'user', icon: 'play', label: 'nxs.act.title' },
  { id: 'sessions', group: 'user', icon: 'monitor', label: 'devices.title' },
  { id: 'appearance', group: 'app', icon: 'palette', label: 'set.appearance' },
  { id: 'voice', group: 'app', icon: 'mic', label: 'ns.set.voice' },
  { id: 'notifications', group: 'app', icon: 'bell', label: 'ns.set.notifications' },
  { id: 'sounds', group: 'app', icon: 'music', label: 'ns.set.sounds' },
  { id: 'server', group: 'app', icon: 'globe', label: 'um.server.title' },
  { id: 'about', group: 'app', icon: 'info', label: 'set.about' },
];

/**
 * A group of settings: an optional caps title over one card (§7.24). List
 * cards hold rows (padding 4 16); `form` cards hold fields (padding 20).
 */
export function SetGroup({
  title,
  form,
  children,
}: {
  title?: string;
  form?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="nx-set-group">
      {title ? <h3>{title}</h3> : null}
      <div className={'nx-card nx-set-card' + (form ? ' form' : '')}>{children}</div>
    </div>
  );
}

/**
 * A settings row: the label (and its description) beside the control; a
 * wide control (segmented, `stack`) takes its own line under the label.
 */
export function SetRow({
  label,
  sub,
  stack,
  children,
}: {
  label: string;
  sub?: string;
  stack?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className={'nx-set-row' + (stack ? ' stack' : '') + (sub ? ' has-s' : '')}>
      <div className="nx-set-rt">
        <div className="l">{label}</div>
        {sub ? <div className="s">{sub}</div> : null}
      </div>
      <div className="nx-set-ctl">{children}</div>
    </div>
  );
}

export { UnsavedBar } from './Layer';

/**
 * Marks the scrolling page `data-more` while part of it is below the fold
 * (live bug 2026-10-28: pages cut off with no hint they continue). The
 * always-visible scrollbar track of a long reader is the hint now (§7.28);
 * the mark stays for the checks that read it (e2e (k2)).
 */
function useMoreBelow(ref: RefObject<HTMLElement | null>, key: string): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const more = el.scrollTop + el.clientHeight < el.scrollHeight - 4;
      if (more) el.dataset.more = '';
      else delete el.dataset.more;
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    ro?.observe(el);
    if (el.firstElementChild) ro?.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', update);
      ro?.disconnect();
    };
  }, [ref, key]);
}

/**
 * The section the general entries open (the dock's gear, Jump, Ctrl+,): a
 * phone shows the list of sections first for it; any other section is a
 * deep link (Voice settings from the room, Edit profile) and opens its page,
 * with Back to the list (§12.7 C1).
 */
export const SETTINGS_ENTRY: SettingsSection = 'appearance';

export function SettingsDialog({ section }: { section: SettingsSection }) {
  const { t } = useT();
  const close = () => goSettings(null);
  const body = useRef<HTMLDivElement>(null);
  // the section it opened on, for the phone's first pane
  const [opened] = useState(section);
  useTopOnChange(body, section);
  useMoreBelow(body, section);
  const tab = (s: (typeof SECTIONS)[number]) => ({ id: s.id, icon: s.icon, label: t(s.label) });
  const title = t(SECTIONS.find((s) => s.id === section)?.label ?? 'set.title');
  return (
    <SetLayer
      cls="nx-set-layer"
      label={t('set.title')}
      onClose={close}
      bodyRef={body}
      page={section}
      pageTitle={title}
      listFirst={opened === SETTINGS_ENTRY}
      nav={
        <SetNav
          label={t('set.title')}
          current={section}
          onPick={(id) => goSettings(id as SettingsSection)}
          groups={[
            { title: t('set.user'), tabs: SECTIONS.filter((s) => s.group === 'user').map(tab) },
            { title: t('set.app'), tabs: SECTIONS.filter((s) => s.group === 'app').map(tab) },
          ]}
        />
      }
    >
      <section key={section} className="nx-set-sec" role="tabpanel" aria-label={title}>
        {section === 'profile' ? <ProfileSection /> : null}
        {section === 'security' ? <SecuritySection /> : null}
        {section === 'privacy' ? <PrivacySection /> : null}
        {section === 'activity' ? <ActivitySection /> : null}
        {section === 'sessions' ? <SessionsSection /> : null}
        {section === 'appearance' ? <AppearanceSection /> : null}
        {section === 'voice' ? <VoiceSection /> : null}
        {section === 'notifications' ? <NotificationsSection /> : null}
        {section === 'notifications' ? <NotifyDeviceSettings /> : null}
        {section === 'notifications' ? <PokeAlertSettings /> : null}
        {section === 'notifications' ? <ServerNotifyLink /> : null}
        {section === 'sounds' ? <SoundsSection /> : null}
        {section === 'server' ? <ServerSection /> : null}
        {section === 'about' ? <AboutSection /> : null}
      </section>
    </SetLayer>
  );
}
